/**
 * HTTP请求封装
 */

import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
} from 'axios';
import { message as antdMessage } from '@/utils/antdStatic';
import type { ApiResponse, RequestConfig } from '@/types/api';
import { getToken, removeToken, removeRefreshToken, removeUserInfo, getUserInfo, getRefreshToken, setToken, setRefreshToken } from './storage';
import { keysToCamel, keysToSnake } from './caseConverter';
import { showDedupError } from './messageDedup';
import { sendTokenRefreshToMall } from './authSync';

// 401 处理防重复标志
let isHandling401 = false;

const isPublicAuthPath = () => {
  if (typeof window === 'undefined') return false;
  return ['/login', '/auth/login', '/tenant/login', '/platform/login'].includes(window.location.pathname);
};

const clearAuthStorage = () => {
  removeToken();
  removeRefreshToken();
  removeUserInfo();
};

// ─── Token 静默刷新 ─────────────────────────────────────────

let refreshingPromise: Promise<boolean> | null = null;

/**
 * 静默刷新 token（互斥锁，防止并发请求同时触发多次刷新）
 */
async function silentRefresh(): Promise<boolean> {
  // 已有刷新请求在飞行中，直接等待
  if (refreshingPromise) return refreshingPromise;

  refreshingPromise = (async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) return false;

    try {
      // 用独立的 axios 请求避免触发拦截器死循环
      const baseURL = import.meta.env.DEV ? '' : import.meta.env.VITE_API_BASE_URL;
      const res = await axios.post(
        `${baseURL}/api/v1/auth/refresh`,
        { refresh_token: refreshToken },
      );
      const data = res.data?.data || res.data;
      const newToken = data?.access_token || data?.accessToken || data?.token;
      const newRefresh = data?.refresh_token || data?.refreshToken;

      if (newToken) {
        setToken(newToken);
        if (newRefresh) setRefreshToken(newRefresh);
        // 通知商城窗口更新 token
        sendTokenRefreshToMall(newToken, newRefresh);
        return true;
      }
      return false;
    } catch {
      // 同域的其他标签页（后台/商城）可能刚轮换过 refresh token，旧的已被吊销：用它存下的新令牌即可
      return getRefreshToken() !== refreshToken;
    }
  })();

  try {
    return await refreshingPromise;
  } finally {
    refreshingPromise = null;
  }
}

/**
 * 从JWT Token中解析payload
 */
const parseJwtPayload = (token: string): { exp?: number; user_scope?: string; tenant_id?: number } | null => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch {
    return null;
  }
};

/**
 * 获取当前用户作用域（platform=平台管理员, tenant=租户用户）
 */
export const getUserScopeFromContext = (): string => {
  // 1. 优先从localStorage的用户信息中获取
  const userInfo = getUserInfo();
  if (userInfo?.user_scope) {
    return userInfo.user_scope;
  }

  // 2. 从JWT token中解析
  const token = getToken();
  if (token) {
    const payload = parseJwtPayload(token);
    if (payload?.user_scope) {
      return payload.user_scope;
    }
  }

  return 'tenant';
};

/**
 * 获取当前租户ID（优先从用户信息获取，否则从JWT token解析）
 */
export const getTenantIdFromContext = (): number | null => {
  // 1. 优先从localStorage的用户信息中获取
  const userInfo = getUserInfo();
  if (userInfo?.tenant_id) {
    return userInfo.tenant_id;
  }

  // 2. 从JWT token中解析
  const token = getToken();
  if (token) {
    const payload = parseJwtPayload(token);
    if (payload?.tenant_id) {
      return payload.tenant_id;
    }
  }

  return null;
};

/**
 * 请求配置
 */
const config = {
  // Always empty — all paths already include /api/v1/ via buildApiPath/ensureApiPrefix.
  // VITE_API_BASE_URL is intentionally empty for Nginx-proxied deployments; if set to a
  // path prefix (e.g. "/api"), Axios combineURLs strips the leading "/" from each request
  // path and joins it to the base, producing double prefixes like "/api/api/v1/...".
  baseURL: '',
  timeout: 60000,
  withCredentials: false,
};

/**
 * 请求状态码处理
 */
const codeMessage: Record<number, string> = {
  200: '服务器成功返回请求的数据。',
  201: '新建或修改数据成功。',
  202: '一个请求已经进入后台排队（异步任务）。',
  204: '删除数据成功。',
  400: '发出的请求有错误，服务器没有进行新建或修改数据的操作。',
  401: '用户没有权限（令牌、用户名、密码错误）。',
  403: '用户得到授权，但是访问是被禁止的。',
  404: '发出的请求针对的是不存在的记录，服务器没有进行操作。',
  406: '请求的格式不可得。',
  410: '请求的资源被永久删除，且不会再得到的。',
  422: '当创建一个对象时，发生一个验证错误。',
  500: '服务器发生错误，请检查服务器。',
  502: '网关错误。',
  503: '服务不可用，服务器暂时过载或维护。',
  504: '网关超时。',
};

/**
 * 创建axios实例
 */
const instance: AxiosInstance = axios.create(config);

/**
 * 请求拦截器
 */
instance.interceptors.request.use(
  async (config) => {
    // 添加认证token（先检查是否需要刷新）
    let token = getToken();
    if (token && config.headers) {
      const payload = parseJwtPayload(token);
      if (payload?.exp) {
        const expiresIn = payload.exp * 1000 - Date.now();
        // token 已过期或 5 分钟内过期 → 用 refresh token 静默刷新
        if (expiresIn < 5 * 60 * 1000) {
          await silentRefresh();
          token = getToken(); // 重新读取（可能已刷新）
        }
      }
      config.headers.Authorization = `Bearer ${token}`;
    }

    // 添加租户ID头（从用户信息或JWT token中获取）
    if (config.headers) {
      const tenantId = getTenantIdFromContext();
      if (tenantId) {
        config.headers['X-Tenant-ID'] = String(tenantId);
      }
    }

    // 添加请求时间戳（防缓存）
    if (config.method === 'get') {
      config.params = {
        ...config.params,
        _t: Date.now(),
      };
    }

    // POST 请求自动添加幂等 key（防重复提交）
    if (config.method === 'post' && !config.headers['X-Idempotency-Key']) {
      config.headers['X-Idempotency-Key'] = crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }

    // 将请求体中的 camelCase 键转换为 snake_case（后端使用snake_case）
    if (config.data && typeof config.data === 'object' && !(config.data instanceof FormData)) {
      config.data = keysToSnake(config.data);
    }

    // 将请求参数中的 camelCase 键转换为 snake_case
    if (config.params && typeof config.params === 'object') {
      config.params = keysToSnake(config.params);
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

/**
 * 响应拦截器
 */
instance.interceptors.response.use(
  (response: AxiosResponse<ApiResponse>) => {
    // 将响应数据中的 snake_case 键转换为 camelCase（后端返回snake_case）
    if (response.data && typeof response.data === 'object') {
      response.data = keysToCamel(response.data);
    }

    const { data } = response;
    const { message: msg, success } = data as { message: string; success?: boolean };
    let code: number | string | undefined = (data as { code?: number | string }).code;

    // 兼容后端返回的 code 可能为 string 类型的数字 ("200" / "0")
    if (typeof code === 'string') {
      const parsed = Number(code);
      // 只有能解析为有效数字的才视为 API 状态码，否则忽略
      // 避免业务字段 code（如分类编码 "vip02"）被误当作状态码
      code = Number.isFinite(parsed) ? parsed : undefined;
    }

    // HTTP 2xx 且响应没有包装格式的 code 字段，认为是成功的原始响应
    if (code === undefined && response.status >= 200 && response.status < 300) {
      return response;
    }

    // 统一处理所有响应
    // code === 200 或 code === 0 都视为成功（兼容不同后端服务的响应格式）
    if ((code === 200 || code === 0) && (success === undefined || success === true)) {
      return response;
    }

    // 其它情况均视为失败，统一提示（使用去重消息避免重复显示）
    switch (code) {
      case 401: {
        if (isPublicAuthPath()) {
          clearAuthStorage();
          return Promise.reject(new Error(msg || '请求失败'));
        }
        // 检查是否是 foundation-service 的 API（这些 API 在本地开发时可能不可用）
        const isFoundationApi = response.config?.url?.includes('/admin-mode/') ||
                               response.config?.url?.includes('/tenant/settings');

        // 如果是 foundation-service 的 API 且本地开发环境，不触发自动退出
        if (isFoundationApi && import.meta.env.DEV) {
          console.warn('Foundation Service API 返回 401，可能服务未启动，跳过自动退出');
          return Promise.reject(new Error('Foundation Service 不可用'));
        }

        // 防止重复处理401
        if (!isHandling401) {
          isHandling401 = true;
          showDedupError('登录状态已过期，请重新登录');
          clearAuthStorage();
          window.location.href = '/login';
        }
        break;
      }
      case 403:
        showDedupError('没有权限访问该资源');
        break;
      case 500:
        showDedupError('服务器内部错误');
        break;
      default:
        if (!(String(response.config?.url || '').includes('/orders'))) {
          showDedupError(msg || '请求失败');
        }
    }

    return Promise.reject(new Error(msg || '请求失败'));
  },
  async (error: AxiosError) => {
    let errorMessage = '网络错误';

    // 401：先用 refresh token 续期并重试一次，续不上再走下面的退出登录
    const originalConfig = error.config as (AxiosRequestConfig & { _retried?: boolean }) | undefined;
    if (error.response?.status === 401 && originalConfig?.headers?.Authorization && !originalConfig._retried && !isPublicAuthPath()) {
      originalConfig._retried = true;
      if (await silentRefresh()) {
        return instance(originalConfig);
      }
    }

    if (error.response) {
      const { status, statusText, data: respData } = error.response;
      // 优先使用响应体中的业务错误信息（error / msg / message 字段）
      const body = respData as { error?: string; msg?: string; message?: string } | undefined;
      const bodyMsg = body?.error || body?.msg || body?.message;
      errorMessage = bodyMsg || codeMessage[status] || statusText || '请求失败';

      // 开发环境下，admin stats API 不存在时静默处理
      if (status === 404 && import.meta.env.DEV) {
        const url = error.config?.url || '';
        if (url.includes('/admin/stats/')) {
          console.warn('Admin Stats API 暂未实现，跳过错误提示:', url);
          return Promise.reject(error);
        }
      }

      // 特殊状态码处理 - 401 需要防重复处理
      if (status === 401) {
        if (isPublicAuthPath()) {
          clearAuthStorage();
          return Promise.reject(error);
        }
        // 防止多个并发401请求重复处理
        if (!isHandling401) {
          isHandling401 = true;
          showDedupError('登录状态已过期，请重新登录');
          clearAuthStorage();
          // 延迟重置标志，避免跳转前其他请求再次触发
          setTimeout(() => { isHandling401 = false; }, 3000);
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }

      // 409 Conflict: 静默传递，由业务层处理（如手机号重复确认覆盖）
      if (status === 409) {
        return Promise.reject(error);
      }
    } else if (error.request) {
      errorMessage = '网络连接失败，请检查网络设置';
    } else {
      errorMessage = error.message || '请求配置错误';
    }

    const suppressBusinessError = error.config?.headers?.['X-Suppress-Error'] === 'true'
      || String(error.config?.url || '').includes('/orders');
    if (!suppressBusinessError) {
      showDedupError(errorMessage);
    }
    return Promise.reject(error);
  }
);

/* eslint-disable @typescript-eslint/no-explicit-any -- 调用方普遍依赖 T 默认 any，改默认值要连带改全部调用方 */
/**
 * 通用请求方法
 */
export const request = async <T = any>(
  config: RequestConfig
): Promise<T> => {
  const response = await instance.request<ApiResponse<T>>(config);
  // 兼容两种响应格式:
  // 1. 包装格式: {code, success, data, message} - 返回 data 字段
  // 2. 直接格式: {captcha_id, ...} - 直接返回整个响应体
  const body = response.data as unknown;
  // 列表响应（如 {data, total, page, limit}）保留完整对象，
  // 避免解包成 data 数组后丢失 total/page 等分页字段。
  const isListResponse = !!body && typeof body === 'object' && ('total' in body || 'page' in body);
  if (body && typeof body === 'object' && 'data' in body && !isListResponse) {
    return (body as { data: unknown }).data as T;
  }
  // auth-service等直接返回数据的情况
  return body as unknown as T;
};

/**
 * GET请求
 */
export const get = <T = any>(
  url: string,
  params?: unknown,
  config?: Omit<RequestConfig, 'url' | 'method' | 'params'>
): Promise<T> => {
  return request<T>({
    url,
    method: 'GET',
    params,
    ...config,
  });
};

/**
 * POST请求
 */
export const post = <T = any>(
  url: string,
  data?: unknown,
  config?: Omit<RequestConfig, 'url' | 'method' | 'data'>
): Promise<T> => {
  return request<T>({
    url,
    method: 'POST',
    data,
    ...config,
  });
};

/**
 * PUT请求
 */
export const put = <T = any>(
  url: string,
  data?: unknown,
  config?: Omit<RequestConfig, 'url' | 'method' | 'data'>
): Promise<T> => {
  return request<T>({
    url,
    method: 'PUT',
    data,
    ...config,
  });
};

/**
 * DELETE请求
 */
export const del = <T = any>(
  url: string,
  params?: unknown,
  config?: Omit<RequestConfig, 'url' | 'method' | 'params'>
): Promise<T> => {
  return request<T>({
    url,
    method: 'DELETE',
    params,
    ...config,
  });
};

/**
 * PATCH请求
 */
export const patch = <T = any>(
  url: string,
  data?: unknown,
  config?: Omit<RequestConfig, 'url' | 'method' | 'data'>
): Promise<T> => {
  return request<T>({
    url,
    method: 'PATCH',
    data,
    ...config,
  });
};

/**
 * 文件上传
 */
export const upload = <T = any>(
  url: string,
  file: File | FormData,
  config?: Omit<RequestConfig, 'url' | 'method' | 'data'>
): Promise<T> => {
  const formData = file instanceof FormData ? file : new FormData();
  if (file instanceof File) {
    formData.append('file', file);
  }

  return request<T>({
    url,
    method: 'POST',
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    ...config,
  });
};

/**
 * 文件下载
 */
export const download = async (
  url: string,
  params?: unknown,
  filename?: string
): Promise<void> => {
  try {
    const response = await instance.get(url, {
      params,
      responseType: 'blob',
    });

    const blob = new Blob([response.data]);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    antdMessage.error('文件下载失败');
    throw error;
  }
};

/* eslint-enable @typescript-eslint/no-explicit-any */

export default request;
